import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../../../i18n';

export interface FastTypingPlayerProps {
  timeLimit: number;
  onSubmit: (answer: string, points?: number) => void;
  /** Called on every input change so parent can track latest typed text (for late-submit on bonus-level-result). */
  onAnswerChange?: (answer: string) => void;
  disabled?: boolean;
  /** When host is also playing, show the text to type (normally visible only on the host display). */
  promptText?: string;
}

/**
 * Player view: textarea to type the text and Submit button (same pattern as WhosOnImage).
 * Auto-submits the current text when disabled becomes true (time up / round ended)
 * so the player's work is never lost.
 */
export const FastTypingPlayer: React.FC<FastTypingPlayerProps> = ({
  timeLimit,
  onSubmit,
  onAnswerChange,
  disabled = false,
  promptText,
}) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState('');
  const submittedRef = useRef(false);
  const answerRef = useRef('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Stable ref for onSubmit so the timer doesn't reset on every parent re-render
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  // Keep ref in sync so auto-submit can read latest value
  useEffect(() => { answerRef.current = answer; }, [answer]);

  const doSubmit = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    // Read from DOM when available (bonus-time-up) so we get the absolute latest input
    const text =
      textareaRef.current?.value?.trim() ?? answerRef.current.trim();
    onSubmitRef.current(text);
  };

  const handleSubmit = () => doSubmit();

  // Primary: auto-submit via local timer at timeLimit (fires before server grace ends)
  useEffect(() => {
    if (submittedRef.current || disabled) return;
    const timer = setTimeout(() => doSubmit(), timeLimit);
    return () => clearTimeout(timer);
  }, [timeLimit, disabled]);

  // Backup: auto-submit when disabled becomes true (round ended from server)
  useEffect(() => {
    if (disabled && !submittedRef.current) doSubmit();
  }, [disabled]);

  return (
    <div style={{ textAlign: 'center', padding: '1rem', width: '100%', maxWidth: 560, margin: '0 auto' }}>
      {promptText ? (
        <div
          style={{
            marginBottom: '1rem',
            padding: '1rem 1.1rem',
            textAlign: 'left',
            fontSize: '1.05rem',
            fontFamily: 'ui-monospace, monospace',
            lineHeight: 1.5,
            color: 'var(--text-primary)',
            background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.12) 100%)',
            border: '2px solid rgba(var(--color-secondary-rgb), 0.32)',
            borderRadius: '12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {promptText}
        </div>
      ) : null}
      <div style={{ marginBottom: '1.5rem' }}>
        <textarea
          ref={textareaRef}
          value={answer}
          onChange={(e) => {
            const v = e.target.value;
            setAnswer(v);
            onAnswerChange?.(v);
          }}
          onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && handleSubmit()}
          placeholder={t('bonus.player.fastTypingPlaceholder')}
          disabled={disabled}
          autoFocus
          autoComplete="off"
          rows={5}
          style={{
            width: '100%',
            padding: '1.2rem',
            fontSize: '1.15rem',
            fontFamily: 'ui-monospace, monospace',
            lineHeight: 1.5,
            resize: 'vertical',
            minHeight: 140,
            background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
            border: '2px solid rgba(var(--color-secondary-rgb), 0.35)',
            borderRadius: '15px',
            color: 'var(--text-primary)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled}
        className="button"
        style={{
          width: '100%',
          background: !disabled
            ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)'
            : 'var(--surface-answer-disabled)',
          color: !disabled ? 'var(--text-on-primary)' : 'var(--text-secondary)',
          border: !disabled ? 'none' : '1px solid rgba(var(--color-secondary-rgb), 0.24)',
          opacity: 1,
          cursor: !disabled ? 'pointer' : 'not-allowed',
        }}
      >
        {t('gameplay.submitAnswer')}
      </button>
    </div>
  );
};
